import { onBeforeUnmount, watch } from "vue";
import { useGuidanceStore } from "../../store/guidance";
import { useMobileNavStore } from "../../store/mobileNav";
import { useBreakpoints } from "./useBreakpoints";

/** Панель навигации телефона — внутри неё живут пункты бокового меню. */
const DRAWER_SELECTOR = ".sidebar__wrapper";

/**
 * Обучение и выезжающее меню телефона (CAB-489).
 *
 * Сценарий обучения писался под компьютер, где боковое меню всегда на экране.
 * На телефоне то же меню — панель, увезённая за левый край: её пункты остаются
 * в разметке и имеют размеры, поэтому обучение честно находило цель и рисовало
 * подсветку по её координатам — за краем экрана, около x = −300. Человек видел
 * затемнение и подсказку, указывающую в пустоту.
 *
 * Открываем меню на тех шагах, где цель внутри него, и закрываем, когда шаг
 * ушёл наружу. Это не обходной путь: на телефоне пункт и правда находится в
 * меню, и показать его иначе нельзя — заодно обучение показывает, где искать.
 */
export function useGuidanceMobileDrawer(): void {
  const guidanceStore = useGuidanceStore();
  const mobileNav = useMobileNavStore();
  const { isMobile } = useBreakpoints();

  /** Открыли меню мы — значит нам его и закрывать. */
  let openedByGuidance = false;

  function stepTargetsDrawer(): boolean {
    const selector = guidanceStore.currentStepConfig?.targetSelector;
    if (!selector) return false;

    const selectors = Array.isArray(selector) ? selector : [selector];
    return selectors.some((one) => {
      try {
        return Array.from(document.querySelectorAll(one)).some((el) => el.closest(DRAWER_SELECTOR) !== null);
      } catch {
        // Селектор из сценария может оказаться некорректным — шаг просто
        // не считается «меню», обучение продолжается без открытия панели.
        return false;
      }
    });
  }

  function sync(): void {
    if (!isMobile.value || !guidanceStore.isActive) {
      closeIfOurs();
      return;
    }

    if (stepTargetsDrawer()) {
      if (!mobileNav.isDrawerOpen) {
        mobileNav.openDrawer();
        openedByGuidance = true;
      }
      return;
    }

    closeIfOurs();
  }

  function closeIfOurs(): void {
    if (!openedByGuidance) return;
    openedByGuidance = false;
    mobileNav.closeDrawer();
  }

  // currentStepConfig — тоже источник: при «Назад» шаг меняется раньше, чем
  // роутер доедет до его страницы, и в этот миг конфиг шага пуст (он привязан к
  // маршруту). Без повторной синхронизации после смены маршрута меню оставалось
  // закрытым, а подсказка указывала в пустоту (CAB-636, замечание 15).
  watch(
    () => [guidanceStore.isActive, guidanceStore.progress.step, guidanceStore.currentStepConfig, isMobile.value],
    () => sync(),
    { immediate: true },
  );

  onBeforeUnmount(closeIfOurs);
}
